// PRE RMA CSB — Client API.
// Phase 4 : appels directs en ligne. La file d'attente hors-connexion
// (point 28-29) est ajoutée en Phase 5 dans offline/syncQueue.ts, qui
// enveloppera post() sans changer cette interface.

import { lireJeton } from '../../../shared/business-logic/jeton.ts';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

function getToken(): string | null {
  return localStorage.getItem('prerma_token');
}
export function setSession(token: string, role: string, csbId: string | null, nomCompte?: string | null) {
  localStorage.setItem('prerma_token', token);
  localStorage.setItem('prerma_role', role);
  localStorage.setItem('prerma_csb_id', csbId ?? '');
  if (nomCompte !== undefined) localStorage.setItem('prerma_nom_compte', nomCompte ?? '');
}
export function clearSession() {
  localStorage.removeItem('prerma_token');
  localStorage.removeItem('prerma_role');
  localStorage.removeItem('prerma_csb_id');
  localStorage.removeItem('prerma_nom_compte');
  for (const k of ['prerma_modules', 'prerma_profil', 'prerma_mdp_a_changer']) localStorage.removeItem(k);
}
export function getSession() {
  // Rôle et CSB : lus dans le JETON (ce que le serveur voit réellement). Les copies séparées du navigateur ne servent que de secours
  // si le jeton est illisible. Évite l'état incohérent « menus Admin affichés mais jeton Responsable CSB » (→ « Rôle insuffisant »).
  const token = getToken();
  const j = lireJeton(token);
  return {
    token,
    role: j?.role ?? localStorage.getItem('prerma_role'),
    csbId: j ? j.csbId : (localStorage.getItem('prerma_csb_id') || null),
    nomCompte: localStorage.getItem('prerma_nom_compte') || null,
  };
}

// Délai maximal d'une requête : 90 s. Les Centres de Santé travaillent en 2G/3G instable : un délai trop court coupe des requêtes
// qui allaient aboutir (l'envoi échoue alors en boucle). Sans aucun délai, fetch peut en revanche attendre des minutes sur un réseau
// « connecté » sans données. Après ce délai : erreur réseau → l'élément reste en file et sera réessayé plus tard.
const DELAI_REQUETE_MS = 90000; // timeout: 90000 (ms) = 90 secondes

/** fetch avec délai maximal. Lève une erreur SANS `status` (= erreur réseau) en cas de coupure ou de dépassement. */
async function fetchAvecDelai(url: string, init: RequestInit): Promise<Response> {
  const controleur = new AbortController();
  const minuteur = setTimeout(() => controleur.abort(), DELAI_REQUETE_MS);
  try {
    return await fetch(url, { ...init, signal: controleur.signal });
  } catch {
    // Pas de `status` : traité comme une erreur RÉSEAU (la file garde l'élément et réessaiera).
    const err = new Error(controleur.signal.aborted ? 'Délai dépassé : connexion trop lente ou instable.' : 'Réseau indisponible.') as Error & Record<string, unknown>;
    err.reseau = true;
    throw err;
  } finally {
    clearTimeout(minuteur);
  }
}

// Renouvellement de session : une seule demande à la fois, partagée par toutes les requêtes qui reçoivent un 401 en même temps.
let renouvellementEnCours: Promise<boolean> | null = null;

/**
 * Tente de renouveler le jeton (POST /auth/refresh). true = nouveau jeton enregistré ; false = session réellement perdue (401/403,
 * il faudra se reconnecter). Une erreur RÉSEAU est propagée : ce n'est pas une session perdue.
 */
function renouvelerSession(): Promise<boolean> {
  renouvellementEnCours ??= (async () => {
    const token = getToken();
    if (!token) return false;
    const res = await fetchAvecDelai(`${API_BASE}/auth/refresh`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } });
    if (!res.ok) return false;
    const d: any = await res.json().catch(() => null);
    if (!d?.token) return false;
    // L'utilisateur s'est reconnecté (autre compte) pendant ce renouvellement : on n'écrase JAMAIS la nouvelle session avec l'ancienne.
    if (getToken() !== token) return getToken() !== null;
    setSession(d.token, d.role ?? localStorage.getItem('prerma_role') ?? '', d.csbId ?? (localStorage.getItem('prerma_csb_id') || null), d.nomCompte);
    return true;
  })().finally(() => { renouvellementEnCours = null; });
  return renouvellementEnCours;
}

async function request<T>(method: string, path: string, body?: unknown, dejaRenouvele = false): Promise<T> {
  const token = getToken(); // relu à CHAQUE appel (et donc après un renouvellement) : jamais un jeton périmé gardé en mémoire
  const res = await fetchAvecDelai(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  // Session glissante : le serveur renvoie un jeton frais quand l'actuel approche de son expiration.
  const renouvele = res.headers.get('X-Renewed-Token');
  // Idem : un jeton renouvelé d'une requête partie avec l'ANCIEN jeton ne remplace pas le jeton d'une session ouverte depuis.
  if (renouvele && res.ok && getToken() === token) localStorage.setItem('prerma_token', renouvele);
  const data: any = await res.json().catch(() => ({}));
  if (!res.ok) {
    // 401 (hors écrans /auth/) : on renouvelle le jeton et on rejoue la requête UNE fois, de façon transparente.
    if (res.status === 401 && !path.startsWith('/auth/') && !dejaRenouvele) {
      let renouvelee = false;
      try { renouvelee = await renouvelerSession(); } catch { /* réseau coupé pendant le renouvellement : on retombe sur l'erreur 401 d'origine */ }
      if (renouvelee) return request<T>(method, path, body, true);
    }
    // Mot de passe initial non changé : le serveur n'accepte plus que le changement de mot de passe.
    if (res.status === 403 && data.code === 'MDP_A_CHANGER') { localStorage.setItem('prerma_mdp_a_changer', '1'); window.dispatchEvent(new Event('prerma:mdp-a-changer')); }
    // Jeton expiré/invalide non renouvelable : l'application l'affiche et propose de se reconnecter SANS rien effacer.
    if (res.status === 401 && !path.startsWith('/auth/')) window.dispatchEvent(new Event('prerma:session-expiree'));
    const err = new Error(data.erreur ?? `Erreur ${res.status}`) as Error & Record<string, unknown>;
    err.status = res.status;
    const retryAfter = Number(res.headers.get('Retry-After'));
    if (retryAfter > 0) err.retryAfter = retryAfter; // secondes (429)
    Object.assign(err, data); // expose ex: details (validation), versionServeur (conflit 409)
    throw err;
  }
  return data as T;
}

// File d'écriture GLOBALE : les requêtes qui modifient des données (POST/PATCH/DELETE hors /auth/) partent UNE PAR UNE dans toute
// l'application, même quand plusieurs files (consultations, CPN, modules) se déclenchent en même temps (retour du réseau, minuteurs…).
// Jamais deux envois en parallèle sur un réseau 2G/3G. Les lectures (GET) ne sont pas concernées. Chaque requête est bornée par le délai
// de 90 s : une requête bloquée ne retient donc jamais les suivantes plus longtemps.
let fileEcritures: Promise<unknown> = Promise.resolve();
function enSerie<T>(tache: () => Promise<T>): Promise<T> {
  const suite = fileEcritures.then(tache, tache);
  fileEcritures = suite.catch(() => undefined);
  return suite;
}
const ecriture = <T>(method: string, path: string, body?: unknown) =>
  path.startsWith('/auth/') ? request<T>(method, path, body) : enSerie(() => request<T>(method, path, body));

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => ecriture<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => ecriture<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => ecriture<T>('PUT', path, body),
  delete: <T>(path: string) => ecriture<T>('DELETE', path),
};
